/**
 * What an ambassador has actually posted about us.
 *
 * Sits beside their Instagram handle, because it is the thing the handle is
 * *for*: a handle nobody has checked is a string, a tag count is a relationship.
 *
 * Counts expand in place rather than opening a second window. A modal inside a
 * modal has to fight the one behind it for focus and for the Escape key, and
 * this is a detail you glance at rather than work in.
 *
 * Queried by handle, not by the collector's stored match, so an ambassador
 * whose handle was recorded a minute ago shows their history at once instead of
 * after the next nightly run.
 */
import { useEffect, useState } from 'react'
import { mentionsForHandle } from '@/features/publishing/repository/mentionsRepository'
import type { Mention } from '@/types/mentions'
import { formatDate } from '@/shared/util/dates'
import './AmbassadorActivity.css'

export function AmbassadorActivity({ handle }: { handle: string }) {
  const [rows, setRows] = useState<Mention[] | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    let live = true
    if (!handle.trim()) {
      setRows([])
      return
    }
    mentionsForHandle(handle)
      .then((r) => live && setRows(r))
      .catch(() => live && setRows([]))
    return () => {
      live = false
    }
  }, [handle])

  if (!handle.trim()) {
    return (
      <p className="ambact__none">
        No Instagram handle recorded, so nothing they post can be attributed to them.
      </p>
    )
  }

  if (rows === null) return <p className="ambact__none">Loading activity…</p>

  return (
    <div className="ambact">
      <div className="ambact__counts">
        <button
          className="ambact__count"
          onClick={() => setOpen((v) => !v)}
          disabled={rows.length === 0}
          aria-expanded={open}
        >
          <span className="ambact__num">{rows.length}</span>
          <span className="ambact__label">
            {rows.length === 1 ? 'post tagging us' : 'posts tagging us'}
          </span>
        </button>
      </div>

      {rows.length === 0 && (
        <p className="ambact__none">
          Nothing yet. Posts from private accounts never reach us — Meta does not return them —
          so silence here is not proof of silence there.
        </p>
      )}

      {open && (
        <div className="ambact__list">
          {rows.map((m) => (
            <div className="ambact__row" key={m.id}>
              <span className="ambact__type">{m.mediaType.toLowerCase() || 'post'}</span>
              <span className="ambact__date">
                {m.postedAt ? formatDate(m.postedAt) : 'no date'}
              </span>
              {m.permalink && (
                <a href={m.permalink} target="_blank" rel="noreferrer">
                  open ↗
                </a>
              )}
              {m.caption && <div className="ambact__caption">{m.caption}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
